import React, { useState, useCallback, useMemo } from 'react';
import { Experience, Content } from '../hooks/api/useExperiences';
import { VideoPlayModal } from './modals/video-play-modal';
import { EventLocations, useHubspotEventMutation } from '../hooks/api/useEvent';
import useAuth from '../hooks/utils/useAuth';
import { PlayIcon } from '../assets/images/svgs';
import { TopicTag } from './topic-tag';
import { useTagGroups } from '../hooks/api/useTagGroups';
import { PeersonaSparkle } from './peersona/peersona-sparkle';
import { usePeersonaAccess } from '../hooks/utils/usePeersonaAccess';
import { AffiliationBanner, getAffiliationClientName } from './affiliation-banner';
import { ExperienceMatchHint, MatchStyleVariant } from './peer-listener-match-hint';

interface VideoExperienceTileProps {
  experience?: Experience | Content;
  handleScheduleModalOpen?: (experience: Experience | Content) => void;
  selectedTagGroupName?: string;
  isLoading?: boolean;
  /** Fired when the user opens the video modal from this tile (e.g. Fi analytics / follow-up). */
  onTileEngage?: () => void;
  showTopicTag?: boolean;
  /** Allow playback without an authenticated user (public collection pages). */
  allowAnonymousPlayback?: boolean;
  matchStyle?: MatchStyleVariant;
}

export const VideoExperienceTileSkeleton: React.FC = () => {
  return (
    <div className="inline-flex w-[194px] flex-col items-start justify-start gap-2">
      <div className="relative flex h-[318px] w-[194px] animate-pulse items-center justify-center overflow-hidden rounded-xl bg-gray-200" />
      <div className="h-6 w-32 animate-pulse rounded bg-gray-200" />
      <div className="flex w-full flex-col gap-2">
        <div className="h-5 w-full animate-pulse rounded bg-gray-200" />
        <div className="h-5 w-3/4 animate-pulse rounded bg-gray-200" />
      </div>
    </div>
  );
};

const getDisplayTagGroup = (tagGroupNames: string[], selectedTagGroupName?: string): string | undefined => {
  if (!tagGroupNames?.length) {
    return undefined;
  }
  if (selectedTagGroupName && tagGroupNames.includes(selectedTagGroupName)) {
    return selectedTagGroupName;
  }
  return tagGroupNames[0];
};

export const VideoExperienceTile: React.FC<VideoExperienceTileProps> = ({
  experience,
  handleScheduleModalOpen,
  selectedTagGroupName,
  isLoading = false,
  onTileEngage,
  showTopicTag = true,
  allowAnonymousPlayback = false,
  matchStyle = 'none',
}) => {
  const { user } = useAuth();
  const hubspotMutation = useHubspotEventMutation();
  const [experienceModal, setExperienceModal] = useState(false);
  const { data: topicTagGroups } = useTagGroups();

  const thumbnailUrl = experience?.vimeo_thumbnail_url || null;
  const videoId = experience?.video_id;

  const tagGroupNames = useMemo(
    () => (experience as Content)?.tag_group_names || (experience as Experience)?.tag_group_names || [],
    [experience],
  );
  const displayTagGroup = useMemo(
    () => getDisplayTagGroup(tagGroupNames, selectedTagGroupName),
    [tagGroupNames, selectedTagGroupName],
  );
  const title = useMemo(
    () => experience?.ai_title || experience?.title || `${experience?.name}'s experience`,
    [experience?.ai_title, experience?.title, experience?.name],
  );

  const { isPeerPeersonaAllowlisted } = usePeersonaAccess();
  const tilePeerListenerRoleId =
    (experience as Experience | undefined)?.listener_role_id ??
    (experience as Content | undefined)?.listener_role_id ??
    null;
  const showPeersonaSparkle = isPeerPeersonaAllowlisted(tilePeerListenerRoleId);

  const handleScheduleModalOpenOnTile = useCallback(
    (exp: Experience | Content) => {
      setExperienceModal(false);
      handleScheduleModalOpen?.(exp);
    },
    [handleScheduleModalOpen],
  );

  const handleThumbnailClick = useCallback(() => {
    if (!experience) return;
    if (!user && !allowAnonymousPlayback) return;

    if (user) {
      hubspotMutation.mutate({
        event: 'click_experience_tile',
        properties: {
          client: user.client_name,
          email_address: user.email_address,
          experience_id: experience.id,
          location: EventLocations.SEARCH,
          media_type: 'video',
        },
      });
    }
    onTileEngage?.();
    setExperienceModal(true);
  }, [user, allowAnonymousPlayback, hubspotMutation, experience, onTileEngage]);

  if (isLoading || !experience) {
    return <VideoExperienceTileSkeleton />;
  }

  if (!videoId) {
    return null;
  }

  const affiliationName = getAffiliationClientName(experience, user?.client_id);

  return (
    <>
      <div className="inline-flex w-[194px] flex-col items-start justify-start gap-2">
        <div
          className="relative isolate flex h-[318px] w-[194px] cursor-pointer items-center justify-center overflow-hidden rounded-xl bg-slate-100"
          onClick={handleThumbnailClick}
        >
          {thumbnailUrl ? (
            <img src={thumbnailUrl} alt={`Video thumbnail for ${title}`} className="h-full w-full object-cover" />
          ) : (
            <div className="flex h-full w-full items-center justify-center bg-gray-300">
              <div className="font-open-sans text-center text-sm text-gray-500">Video thumbnail unavailable</div>
            </div>
          )}
          {thumbnailUrl && (
            <>
              <div className="absolute bottom-3 left-3">
                <img src={PlayIcon} alt="Play video" className="h-12 w-12 opacity-80" />
              </div>
            </>
          )}

          {affiliationName && <AffiliationBanner clientName={affiliationName} />}

          {experience?.name && (
            <div
              className={`absolute left-3 z-10 flex h-6 items-center justify-center gap-1 rounded-[5px] px-3 ${
                affiliationName ? 'top-9 bg-[rgb(var(--color-primary))]' : 'top-3 bg-white/80'
              }`}
              aria-label={showPeersonaSparkle ? `${experience.name} (Peersona)` : experience.name}
            >
              {showPeersonaSparkle && (
                <PeersonaSparkle size={12} className={affiliationName ? 'text-white' : 'text-[#6B6FFF]'} />
              )}
              <div
                className={`font-albert-sans text-[14px] font-medium leading-[18px] ${
                  affiliationName ? 'text-white' : 'text-primary'
                }`}
              >
                {experience.name}
              </div>
            </div>
          )}
        </div>
        {showTopicTag && displayTagGroup ? (
          <TopicTag tagGroupName={displayTagGroup} tagGroups={topicTagGroups} />
        ) : null}
        <div className="font-open-sans line-clamp-2 w-full self-stretch text-base font-semibold leading-[22px] text-black">
          {title}
        </div>
        {experience ? <ExperienceMatchHint match={experience as Content} variant={matchStyle} /> : null}
      </div>

      {experienceModal && (
        <VideoPlayModal
          experience={experience}
          open={experienceModal}
          onClose={() => setExperienceModal(false)}
          handleScheduleModalOpen={handleScheduleModalOpenOnTile}
          selectedTagGroupName={selectedTagGroupName}
        />
      )}
    </>
  );
};
